import { useActionDispatcher, useApi } from "@judo/actions";
import {
	DispatchProvider,
	useApplication,
	useDataStore,
	useNavigation,
	usePageContext,
	useResolvedPageActionOverrides,
} from "@judo/core";
import { useNotifications } from "@judo/feedback";
import type { Flex, PageContainer, PageDefinition } from "@judo/model-api";
import { Box } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { FlexRenderer } from "../renderers/FlexRenderer";
import { PageHeader } from "../renderers/PageHeader";
import { findOnInitAction } from "../utils/find-on-init-action";

export interface FormContainerRendererProps {
	page: PageDefinition;
}

/**
 * Renderer for FORM type containers.
 * Handles onInit action (GetTemplate) on mount to fetch default values for new transfers.
 */
export function FormContainerRenderer({ page }: FormContainerRendererProps) {
	const container = page.container as PageContainer;

	// Get page context to access transfer ID and params
	const pageContext = usePageContext();

	// Get required contexts for action dispatcher
	const navigation = useNavigation();
	const dataStore = useDataStore();
	const application = useApplication();
	const api = useApi();
	const notifications = useNotifications();

	// Get resolved page action overrides from customizations
	const actionOverrides = useResolvedPageActionOverrides();

	// Find the onInit action
	const onInitAction = useMemo(() => findOnInitAction(page, container), [page, container]);

	// Extract create dialog context from page params
	const isCreate = pageContext?.params?.isCreate === true;
	const isEager = pageContext?.params?.isEager === true;
	const relation = pageContext?.params?.relation;
	const ownerTransfer = pageContext?.params?.ownerTransfer;
	// For eager creates, extract parent context to know where to add created transfer
	const parentTransferId = pageContext?.params?.parentTransferId as string | undefined;
	const relationName = pageContext?.params?.relationName as string | undefined;

	// Memoize dispatcher context
	const dispatcherContext = useMemo(
		() =>
			({
				navigation,
				data: dataStore,
				registry: application,
				api,
				notifications,
				additionalContext: {
					pageTransferId: pageContext?.transferId,
					pageType: "FORM",
					isEditMode: true,
					isEager,
					// Pass create dialog context for CreateActionHandler
					createDialog: isCreate
						? {
								relation,
								ownerTransfer,
								isEager,
								// For eager creates, include parent context
								parentTransferId,
								relationName,
							}
						: undefined,
				},
				actionOverrides,
			}) as unknown as Parameters<typeof useActionDispatcher>[0],
		[
			navigation,
			dataStore,
			application,
			api,
			notifications,
			pageContext?.transferId,
			isCreate,
			isEager,
			relation,
			ownerTransfer,
			parentTransferId,
			relationName,
			actionOverrides,
		]
	);

	// Initialize action dispatcher
	const { dispatch } = useActionDispatcher(dispatcherContext);

	// Use ref to track if onInit has been dispatched (avoid duplicate calls on strict mode re-renders)
	const hasDispatchedOnInit = useRef(false);
	// Use ref to store dispatch function to avoid it changing in effect deps
	const dispatchRef = useRef(dispatch);
	dispatchRef.current = dispatch;

	// Call onInit (GetTemplate) when form mounts to fetch default values
	useEffect(() => {
		if (hasDispatchedOnInit.current) return;

		if (onInitAction) {
			hasDispatchedOnInit.current = true;

			// Dispatch the GetTemplate action to fetch default values
			dispatchRef.current(onInitAction).catch((error) => {
				console.error("[FormContainerRenderer] Error dispatching onInit action:", error);
			});
		}
	}, [onInitAction]);

	const firstChild = container.children?.[0] as Flex | undefined;

	// Don't render PageHeader in dialogs - dialog handles its own title and actions
	const showPageHeader = !pageContext?.isDialog;

	return (
		<DispatchProvider dispatch={dispatch}>
			<Box
				data-testid={`form-container::${container.name ?? container["xmi:id"]}`}
				sx={{ animation: "judoFadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) both" }}
			>
				{showPageHeader && <PageHeader page={page} onDispatch={dispatch} />}

				{/* Form content - render children from visual model */}
				{firstChild && <FlexRenderer element={firstChild} readOnly={false} />}
			</Box>
		</DispatchProvider>
	);
}

export default FormContainerRenderer;
